import d3 from 'd3';
import _ from 'lodash';
import MultipleFacetView from './MultipleFacetView';

export default class TagFacetView extends MultipleFacetView {

  constructor(config, $el, margins) {
    super(config, $el, margins);
    this._title = 'Tags Chart';
    this._break = null;
    this._search = '';
  }

  _initGetSetters() {
    super._initGetSetters();
    this.search = (value) => this._getSetter(value, '_search', this._updateData);
  }

  _updateGroups() {
    super._updateGroups();
    this._bgGroup = this._updateBackgroundGroup(this._g);
    this._dataGroup = this._updateDataGroup(this._g);
    this._labelsGroup = this._updateLabelsGroup(this._g);
  }

  _updateBackgroundGroup(parent) {
    const group = parent.selectAll('g.bg').data(['g']);
    group.enter().append('g')
      .attr('class', 'bg')
      .style('cursor', 'crosshair')
      .on('mouseleave', d => this._onMouseLeave(d));
    return group;
  }

  _updateDataGroup(parent) {
    const group = parent.selectAll('g.data').data(['g']);
    group.enter().append('g')
      .attr('class', 'data')
      .style('cursor', 'crosshair');
    return group;
  }

  _updateLabelsGroup(parent) {
    const group = parent.selectAll('g.labels').data(['g']);
    group.enter().append('g')
      .attr('class', 'labels')
      .style('pointer-events', 'none');
    return group;
  }

  _contains(haystack, needle) {
    if (!needle) return true;
    return haystack.indexOf(needle) !== -1;
  }

  _updateData() {
    if (!this._facet || !this._facet.groups()) return;
    this._allData = this._facet.groups()
      // .filter(item => this._xValue(item))
      .sort((itema, itemb) => this._xValue(itema) > this._xValue(itemb) ? -1 : 1);
    const prunedSelection = this._allData.reduce((memo, el) => {
      if (this._selectedMap[this._yValue(el)]) memo[this._yValue(el)] = true;
      return memo;
    }, {});
    this._data = [];
    this._breaks = [];
    if (this._limit) {
      let notSelectedNum = this._limit - _.size(prunedSelection);
      notSelectedNum = Math.max(notSelectedNum, 0);
      for (let i = 0; i < this._allData.length; i++) {
        const el = this._allData[i];
        if (prunedSelection[this._yValue(el)]) {
          // if the last element of the visualization is not the item before the current one in the data, add a break line.
          const previousElement = i ? this._allData[i - 1] : null;
          const lastElement = this._data.length ? this._data[this._data.length - 1] : null;
          if (previousElement && lastElement && this._yValue(previousElement) !== this._yValue(lastElement)) this._breaks.push(this._yValue(lastElement));
          this._data.push(el);
        } else if (this._contains(this._yLabel(el).toLowerCase(), this._search.toLowerCase()) && notSelectedNum) {
          this._data.push(el);
          notSelectedNum--;
        }
        if (this._data.length === this._limit) break;
      }
    } else {
      this._data = this._allData;
    }
    this._updateScales();
  }

  // override
  _updateScales() {
    if (!this._xValue || !this._yValue || !this._data) return;

    var h = (this._data.length / this._limit) * this._height;
    this._yScale = this._yScaleFunction
      .domain(this._data.map(this._yValue))
      .rangeRoundBands([0, h], 0.15);

    this._xMax = d3.max(this._data, this._xValue);
    this._xScale = this._xScaleFunction.domain([0, this._xMax]).rangeRound([0, this._width]);
    // this._xAxis = d3.svg.axis().scale(this._xScale).ticks(5).orient('left');
    // this._xAxisGroup.call(this._xAxis);

    this._drawChart();
  }

  _drawChart() {
    this._drawBackground();
    this._drawData();
    this._drawLabels();
  }

  // override
  _drawBackground() {
    const bgBars = this._bgGroup.selectAll('rect').data(this._data, this._yValue);

    bgBars.enter()
      .append('rect')
      .style('stroke-width', 0)
      .on('mouseover', d => this._onMouseOver(d))
      .on('mouseout', d => this._onMouseOut(d))
      .on('mousedown', d => this._onMouseDown(d))
      .on('mouseup', d => this._onMouseUp(d));

    bgBars.attr('y', d => this._yMap(d))
      .attr('x', 0)
      .attr('height', this._yScale.rangeBand())
      .attr('width', this._width)
      .style('fill', d => this._isSelected(d) ? this._selectedUpColorBg : this._upColorBg);

    bgBars.exit().remove();

    const breakLine = this._bgGroup.selectAll('line').data(this._breaks);
    breakLine.enter().append('line');
    breakLine.exit().remove();

    breakLine
      .attr('x1', 0)
      .attr('x2', this._width)
      .attr('y1', d => this._yScale(d) + this._yScale.rangeBand() + 1)
      .attr('y2', d => this._yScale(d) + this._yScale.rangeBand() + 1)
      .style('stroke', this._selectedUpColor)
      .style('stroke-dasharray','2 2')
      .style('display', d => this._yScale(d) + this._yScale.rangeBand() + 10 > this._height ? 'none' : '');
  }

  // override
  _drawData() {
    const bars = this._dataGroup.selectAll('rect').data(this._data, this._yValue);

    bars.enter()
      .append('rect')
      .on('mouseover', d => this._onMouseOver(d))
      .on('mouseout', d => this._onMouseOut(d))
      .on('mousedown', d => this._onMouseDown(d))
      .on('mouseup', d => this._onMouseUp(d));

    bars.attr('y', d => this._yMap(d))
      .attr('x', 0)
      .attr('width', d => this._xMap(d))
      .style('fill', d => this._isSelected(d) ? this._selectedUpColor : this._upColor)
      .attr('height', this._yScale.rangeBand());

    bars.exit().remove();
  }

  // override
  _drawLabels() {
    const labels = this._labelsGroup.selectAll('text').data(this._data, this._yValue);

    labels.enter()
      .append('text')
      .attr('class', 'taglabel')
      .style('alignment-baseline', 'middle');

    labels
      .text(d => `${this._yLabel(d)}: ${this._xFormat(this._xValue(d))}`)
      .attr('y', d => this._yMap(d) + (this._yScale.rangeBand() / 2))
      .attr('x', d => this._xMap(d) > this._width / 2 ? this._xMap(d) - 3 : this._xMap(d) + 3)
      .style('fill', d => {
        const color = this._isSelected(d) ? '_selectedUpColor' : '_upColor';
        return this._xMap(d) > this._width / 2 ? this[`${color}Bg`] : this[color];
      })
      .style('text-anchor', d => this._xMap(d) > this._width / 2 ? 'end' : 'start');

    labels.exit().remove();
  }

  _drawSelection(d, state) {
    let color;
    if (state === 'over') color = this._isSelected(d) ? '_selectedOverColor' : '_overColor';
    else if (state === 'down') color = this._isSelected(d) ? '_selectedDownColor' : '_downColor';
    else if (state === 'up') color = this._isSelected(d) ? '_selectedUpColor' : '_upColor';
    this._dataGroup.selectAll('rect').data([d], this._yValue).style('fill', this[color]);
    this._bgGroup.selectAll('rect').data([d], this._yValue).style('fill', this[`${color}Bg`]);
    this._labelsGroup.selectAll('text').data([d], this._yValue)
      .style('fill', dd => this._xMap(dd) > this._width / 2 ? this[`${color}Bg`] : this[color]);
  }

  _select(d, value) {
    if (value) this._selectedMap[this._yValue(d)] = true;
    else delete this._selectedMap[this._yValue(d)];
  }

  _isSelected(d) {
    return this._selectedMap[this._yValue(d)] !== undefined;
  }

  _onExtentSelected() {
    let values = Object.keys(this._selectedMap);
    if (values.length === this._allData.length) {
      this._selectedMap = {};
      values = null;
    }

    if (!values || !values.length) this._facet.clear();
    else this._facet.filterMultiple(values);
  }

}



/** WEBPACK FOOTER **
 ** D:/2016/2016 - ISI/grantorino/frontend/../../../../Marco/Dropbox (ISI Foundation)/2016 - ISI/grantorino/frontend/src/js/views/sidebar/archive/facets/TagFacetView.es6
 **/